import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Flag, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../lib/api";
import { PageHeader, EmptyState, DifficultyBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D", "E"];
const FILTERS = [
  { value: "incorrect", label: "Incorrect" },
  { value: "correct", label: "Correct" },
  { value: "flagged", label: "Flagged" },
  { value: "recent", label: "Recent" },
];

function ReviewCard({ item }) {
  const [open, setOpen] = useState(false);
  const q = item.question;
  return (
    <div className="bg-card border border-border rounded-lg" data-testid={`review-card-${q.qid}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4 flex items-start gap-3">
        {item.flagged ? <Flag className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          : item.last_correct === true ? <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
          : item.last_correct === false ? <XCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          : null}
        <div className="flex-1">
          <p className="text-sm font-medium leading-relaxed line-clamp-2">{q.stem}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span>{q.qid}</span><span>·</span><span>{TOPIC_NAMES[q.topic] || q.topic}</span><span>·</span>
            <DifficultyBadge level={q.difficulty} /><span>·</span>
            <span>Attempted {item.attempts}×</span>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-border pt-4 space-y-4">
          <div className="space-y-2">
            {LETTERS.map((l) => (
              <p key={l} className={`text-sm ${l === q.correct_answer ? "text-success font-semibold" : "text-muted-foreground"}`}>
                {l}. {q.options[l]}{l === q.correct_answer && " ✓ correct"}
              </p>
            ))}
          </div>
          <div className="bg-background border border-border rounded-md p-4">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1.5">Why {q.correct_answer} is correct</p>
            <p className="text-sm leading-relaxed">{q.explanation_correct}</p>
          </div>
          <div className="bg-background border border-border rounded-md p-4 space-y-2">
            {LETTERS.filter((l) => l !== q.correct_answer).map((l) => (
              <p key={l} className="text-sm leading-relaxed">
                <span className="font-semibold">{l}:</span>{" "}
                <span className="text-muted-foreground">{q.option_explanations?.[l]}</span>
              </p>
            ))}
          </div>
          {q.key_learning_point && (
            <p className="text-sm bg-secondary border border-border rounded-md p-4">
              <span className="font-semibold">Key point: </span>{q.key_learning_point}
              {q.reference && <span className="block mt-1.5 text-xs text-muted-foreground">Reference: {q.reference}</span>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function GuideFlagCard({ g }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/app/guides/${g.id}`)} data-testid={`flagged-guide-${g.id}`}
      className="w-full text-left bg-card border border-border rounded-lg p-5 hover:border-primary/40 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
        <Flag className="w-4 h-4 text-accent" />
        <span>{TOPIC_NAMES[g.topic] || g.topic}</span>
        <span>·</span><span>Revision guide</span>
      </div>
      <p className="font-medium">{g.title}</p>
      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.excerpt}…</p>
    </button>
  );
}

export default function Review() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("incorrect");
  const [mode, setMode] = useState("questions");
  const [items, setItems] = useState(null);

  useEffect(() => {
    setItems(null);
    const req = mode === "guides"
      ? API.get("/guides/flagged")
      : API.get(`/review?filter=${filter}`);
    req.then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, [filter, mode]);

  const practiseIncorrect = async () => {
    try {
      const { data } = await API.post("/sessions", {
        topic: "all", count: 20, difficulty: "mixed", status_filter: "incorrect", mode: "quiz",
      });
      sessionStorage.setItem(`pamind_session_${data.session_id}`, JSON.stringify(data));
      navigate(`/app/session/${data.session_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="review-page" className="max-w-4xl">
      <PageHeader title="Review" sub="Work through your mistakes, flagged questions and recent history.">
        {mode === "questions" && (
          <Button data-testid="review-practise-incorrect-btn" onClick={practiseIncorrect}>
            <RotateCcw className="w-4 h-4 mr-2" /> Practise my incorrect questions
          </Button>
        )}
      </PageHeader>
      <div className="mb-5 w-72">
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="review-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="questions" data-testid="review-mode-questions">Questions</SelectItem>
            <SelectItem value="guides" data-testid="review-mode-guides">Revision guides (flagged)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "questions" && (
      <div className="flex flex-wrap gap-2.5 mb-6">
        {FILTERS.map((f) => (
          <button key={f.value} data-testid={`review-filter-${f.value}`} onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card hover:border-primary/50"}`}>
            {f.label}
          </button>
        ))}
      </div>
      )}
      {items === null ? (
        <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Nothing here yet"
          body={mode === "guides"
            ? "No flagged revision guides. Flag a guide while reading it and it will appear here."
            : filter === "incorrect" ? "No incorrectly answered questions — great work. Try a new session to keep progressing." : "Questions will appear here as you practise."}
          testid="review-empty-state">
          {mode === "questions" && <Button data-testid="review-start-btn" onClick={() => navigate("/app/topics")}>Start a session</Button>}
        </EmptyState>
      ) : mode === "guides" ? (
        <div className="space-y-4" data-testid="review-guides-list">
          {items.map((g) => <GuideFlagCard key={g.id} g={g} />)}
        </div>
      ) : (
        <div className="space-y-4" data-testid="review-list">
          {items.map((item) => <ReviewCard key={item.question._id} item={item} />)}
        </div>
      )}
    </div>
  );
}
